import React, { useEffect, useRef, useState } from 'react';
import { Alert, Box, Button, Chip, LinearProgress, Skeleton, Stack, Typography } from '@mui/material';
import MspAnalyticsApi, { AutomationBand, TicketAutomationSummary, TicketAutomationAssessment } from '../apis/mspAnalyticsApi';
import { getApiErrorMessage } from '../utils/apiErrorMessage';

export const automationBands: Array<{ key: AutomationBand; label: string; color: string }> = [
  { key: 'high', label: 'High', color: 'success.main' }, { key: 'medium', label: 'Medium', color: 'warning.main' },
  { key: 'low', label: 'Low', color: 'text.secondary' }, { key: 'insufficient', label: 'Insufficient detail', color: 'info.main' },
];

export const visibleFitBands = automationBands.filter(band => band.key === 'high' || band.key === 'medium');

export default function RecentTicketAutomationPanel({ days, ready, generation, total, windowEnd, customerIds=[], queueIds=[], filteredTotal, customerTotals, canDrill=true, onUpdate, onDrill }: {
  customerTotals?: Record<string,number>; customerIds?: string[]; queueIds?: string[]; filteredTotal?: number; days: number; ready: boolean; generation?: string | null; total?: number; windowEnd?: string; canDrill?: boolean; onUpdate: (data: TicketAutomationSummary) => void;
  onDrill: (band: AutomationBand) => void;
}) {
  const [data,setData]=useState<TicketAutomationSummary|null>(null), [error,setError]=useState<string|null>(null);
  const [retryVersion,setRetryVersion]=useState(0);
  const force=useRef(false);
  const latest=useRef<TicketAutomationSummary|null>(null);
  const cache=useRef(new Map<number,TicketAutomationSummary>());
  useEffect(()=>{
    let previous=cache.current.get(days) || latest.current;
    if(previous && (previous.days!==days || (total!==undefined && previous.total!==total)))previous=seedWindow(previous,days,total,windowEnd);
    if(previous){setData(previous);onUpdate(previous);cache.current.set(days,previous);latest.current=previous;}
    if (!ready) return;
    let cancelled=false, timer:number|undefined;
    const controller=new AbortController();
    const apply=(value:TicketAutomationSummary)=>{if(!cancelled){
      setData(value);onUpdate(value);latest.current=value;cache.current.set(days,value);setError(null);
      if(value.end_at && !value.pending && !value.failed && value.ready===value.total) {
        const end=new Date(value.end_at).getTime();
        for(const window of [1,7,14].filter(window=>window<days)) {
          const tickets=Object.fromEntries(Object.entries(value.tickets || {}).filter(([,ticket])=>{const created=new Date(ticket.create_date || '').getTime();return created>=end-window*86400000 && created<=end;}));
          const bands={high:0,medium:0,low:0,insufficient:0};for(const ticket of Object.values(tickets))bands[ticket.band]++;
          cache.current.set(window,{...value,days:window,total:Object.keys(tickets).length,ready:Object.keys(tickets).length,tickets,bands,customer_stats:completedCustomerStats(tickets),scope_stats:completedScopeStats(tickets)});
        }
      }
    }};
    const run=async()=>{
      let pending=false;
      try {
        const retry=force.current;force.current=false;
        await MspAnalyticsApi.streamTicketAutomation(days,event=>{
          if(cancelled)return;
          if(event.type==='automation'){if(event.data.disabled){setError('Fit assessment is not configured.');return;}pending=!!event.data.pending;apply(event.data);}
          else if(event.type==='progress')pending=true;
          else if(event.type==='error')throw new Error('Fit assessment is unavailable. Ticket categories remain available.');
        },controller.signal,retry);
      } catch(err){if(!cancelled){setError(getApiErrorMessage(err,'Unable to assess fit.'));pending=true;}}
      finally{if(!cancelled&&pending)timer=window.setTimeout(()=>void run(),10000);}
    };
    void run();
    return()=>{cancelled=true;controller.abort();if(timer)window.clearTimeout(timer);};
  },[days,ready,generation,total,windowEnd,onUpdate,retryVersion]);
  const unfiltered=data?.days===days?data:(cache.current.get(days) || (data?seedWindow(data,days,total,windowEnd):null));
  const current=unfiltered && (customerIds.length || queueIds.length)?filterAutomationSummary(unfiltered,customerIds,customerTotals,queueIds,filteredTotal):unfiltered;
  return <Box sx={{my:3,typography:'body2'}}>
    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{mb:1}}>
      <Typography variant="h6">Fit</Typography>
      <Typography variant="caption" color="text.secondary">{current?`${current.ready || 0} of ${current.total || 0} assessed`:'Waiting for tickets'}</Typography>
    </Stack>
    <Box sx={{minHeight:4,mb:1}}>{(!current || current.pending>0)&&!error&&<LinearProgress aria-label="Assessing fit" variant={current?'determinate':'indeterminate'} value={current?.total?(current.ready+current.failed)/current.total*100:0}/>}</Box>
    <Box sx={{display:'grid',gridTemplateColumns:{xs:'1fr 1fr',md:'repeat(2,1fr)'},gap:2}}>
      {visibleFitBands.map(band=><Button key={band.key} disabled={!current?.bands?.[band.key] || !canDrill} onClick={()=>onDrill(band.key)}
        sx={{display:'block',textAlign:'left',p:2,bgcolor:'action.hover',color:'text.primary',textTransform:'none'}}>
        <Stack direction="row" gap={1} alignItems="center"><Box sx={{width:9,height:9,borderRadius:'50%',bgcolor:band.color}} />{band.label}</Stack>
        {current && (current.ready>0 || !current.pending)?<><Typography variant="h5" sx={{mt:1}}>{current.bands?.[band.key] || 0}</Typography><Typography variant="body2" color="text.secondary">{current.total?((current.bands?.[band.key] || 0)/current.total*100).toFixed(1):0}% of tickets</Typography></>:<Skeleton width="60%" height={45}/>}
      </Button>)}
    </Box>
    {error&&<Alert severity="warning" sx={{mt:1}}>{error}</Alert>}
    {!!current?.failed&&<Button onClick={()=>{force.current=true;setRetryVersion(value=>value+1);}}>Retry failed assessments</Button>}
  </Box>;
}

export function TicketAutomationContext({assessment}:{assessment?:TicketAutomationAssessment}) {
  if(!assessment)return null;
  return <Box sx={{typography:'body2'}}><Chip size="small" label={`${automationBands.find(band=>band.key===assessment.band)?.label} fit`}/>
    <Typography variant="body2" sx={{mt:1}}>{assessment.reason}</Typography>
  </Box>;
}

function seedWindow(previous:TicketAutomationSummary,days:number,total?:number,windowEnd?:string):TicketAutomationSummary {
  const end=new Date(windowEnd || previous.end_at || Date.now()).getTime();
  const tickets=Object.fromEntries(Object.entries(previous.tickets || {}).filter(([,ticket])=>{const created=new Date(ticket.create_date || '').getTime();return created>=end-days*86400000 && created<=end;}));
  const bands={high:0,medium:0,low:0,insufficient:0};for(const ticket of Object.values(tickets))bands[ticket.band]++;
  const ready=Object.keys(tickets).length, count=Math.max(total ?? ready,ready);
  return {...previous,days,total:count,ready,pending:count-ready,failed:0,bands,tickets,customer_stats:completedCustomerStats(tickets),scope_stats:completedScopeStats(tickets),partial_scope:true};
}


function completedCustomerStats(tickets:Record<string,TicketAutomationAssessment>) {
  const stats:NonNullable<TicketAutomationSummary['customer_stats']>={};
  for(const ticket of Object.values(tickets)){const row=stats[ticket.company_id || 'unknown'] ||= {total:0,ready:0,failed:0};row.total++;row.ready++;}
  return stats;
}

export function filterAutomationSummary(summary:TicketAutomationSummary,customerIds:string[],customerTotals?:Record<string,number>,queueIds:string[]=[],filteredTotal?:number):TicketAutomationSummary {
  const tickets=Object.fromEntries(Object.entries(summary.tickets || {}).filter(([,ticket])=>(!customerIds.length || customerIds.includes(ticket.company_id || 'unknown')) && (!queueIds.length || queueIds.includes(ticket.queue_id || 'unknown'))));
  const bands={high:0,medium:0,low:0,insufficient:0};for(const ticket of Object.values(tickets))bands[ticket.band]++;
  const scoped=summary.partial_scope?undefined:summary.scope_stats?.filter(row=>(!customerIds.length || customerIds.includes(row.company_id)) && (!queueIds.length || queueIds.includes(row.queue_id)));
  const stats=scoped || customerIds.map(id=>summary.customer_stats?.[id]).filter(Boolean);
  const ready=Object.keys(tickets).length,total=scoped ? scoped.reduce((sum,row)=>sum+row.total,0) : filteredTotal ?? customerIds.reduce((sum,id)=>sum+(customerTotals?.[id] ?? summary.customer_stats?.[id]?.total ?? 0),0),failed=stats.reduce((sum,row)=>sum+(row?.failed || 0),0);
  return {...summary,tickets,bands,ready,total:Math.max(total,ready),failed,pending:Math.max(0,total-ready-failed)};
}

function completedScopeStats(tickets:Record<string,TicketAutomationAssessment>) {
  const stats=new Map<string,{company_id:string;queue_id:string;total:number;ready:number;failed:number}>();
  for(const ticket of Object.values(tickets)){
    const company_id=ticket.company_id || 'unknown',queue_id=ticket.queue_id || 'unknown',key=JSON.stringify([company_id,queue_id]);
    if(!stats.has(key))stats.set(key,{company_id,queue_id,total:0,ready:0,failed:0});
    const row=stats.get(key)!;row.total++;row.ready++;
  }
  return Array.from(stats.values());
}
